import { QueryClient } from '@tanstack/react-query';

import { qk } from './query-keys';

/**
 * الغلاف الشبكي الوحيد للواجهة. كل نداء REST يمر من هنا:
 * - `credentials: 'include'` — المصادقة بجلسة الكوكي `__Host-flow_session` وحدها،
 *   فلا token ولا ترويسة تفويض في العميل إطلاقًا (قرار D47).
 * - كل خطأ من الخادم بصيغة RFC 9457 (`application/problem+json`) يُحوَّل إلى
 *   `FlowApiError` حاملًا `code` و`detail_ar` — النص العربي يُعرض للمالك كما هو
 *   ولا تُصاغ رسائل خطأ في العميل (backend-api §8).
 * - `If-Match` يُرسل **دائمًا** من الويب عند وجود revision (قرار D70).
 *
 * `api-client.ts` المولَّد من سجل الأدوات (§1.5) يُبنى فوق `request` هنا ولا يفتح
 * قناة fetch ثانية.
 */

export const API_BASE = '/api/v1';

/** غلاف الترقيم القانوني لكل قوائم REST (backend-api §6) — معرَّف في dto.ts وحده. */
export type { Page } from './dto';

/** جسم الخطأ كما تصفه backend-api §8 — امتدادا RFC 9457 هما code وdetail_ar. */
export interface Problem {
  readonly type?: string;
  readonly title?: string;
  readonly status?: number;
  readonly code?: string;
  readonly detail_ar?: string;
  readonly errors?: readonly { path: string; message_ar: string; rule: string }[];
  readonly request_id?: string;
  /** الحالة الحديثة للكيان في 409 FLOW_REVISION_CONFLICT (قرار D37). */
  readonly [key: string]: unknown;
}

/** رمز يُنتجه العميل وحده (ليس من جدول أكواد الخادم): فشل شبكة قبل أي استجابة. */
export const FLOW_NETWORK = 'FLOW_NETWORK';

export class FlowApiError extends Error {
  readonly code: string;
  readonly detailAr: string;
  readonly status: number;
  readonly problem: Problem | undefined;

  constructor(args: {
    code: string;
    detailAr: string;
    status: number;
    problem?: Problem | undefined;
  }) {
    super(`${args.code} (${args.status}): ${args.detailAr}`);
    this.name = 'FlowApiError';
    this.code = args.code;
    this.detailAr = args.detailAr;
    this.status = args.status;
    this.problem = args.problem;
  }

  /** 409 من تعارض الـ revision يحمل الحالة الحديثة — تُكتب في الكاش (§1.4). */
  get isRevisionConflict(): boolean {
    return this.code === 'FLOW_REVISION_CONFLICT';
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isOffline(): boolean {
    return this.code === FLOW_NETWORK;
  }
}

export interface RequestOptions {
  readonly method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  readonly body?: unknown;
  /** revision الحالي من الكاش — يُترجم إلى ترويسة If-Match (قرار D70). */
  readonly ifMatch?: number | string;
  readonly signal?: AbortSignal;
  readonly query?: Readonly<Record<string, string | number | boolean | undefined>>;
  /** لرفع الوسائط: FormData يُرسل كما هو بلا ترويسة Content-Type. */
  readonly formData?: FormData;
}

function buildUrl(path: string, query: RequestOptions['query']): string {
  const url = `${API_BASE}${path}`;
  if (query === undefined) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined) continue;
    params.set(key, String(value));
  }
  const qs = params.toString();
  return qs === '' ? url : `${url}?${qs}`;
}

/** نقطة فحص الجلسة نفسها — تُستثنى من إبطال الجلسة أدناه كي لا تُبطل نفسها بلا نهاية. */
const AUTH_SESSION_PATH = '/auth/session';

/**
 * نصٌّ عربي **حين لا يرسل الخادم واحدًا** — والحالات التي تصل بلا `detail_ar` ليست
 * نادرة ولا نظرية: هي بالضبط ما لا يجيبه التطبيق نفسه.
 *
 * أوضحها **كل نشرة**: `deploy.sh` يعيد إنشاء `flow-core`، فيردّ Caddy 502 ثوانيَ
 * والمالك أمام الشاشة. «حدث خطأ غير متوقع» تجعله يظن أن عمله ضاع أو أن النظام تعطّل،
 * بينما الصواب «انتظر لحظات». وقاعدة الرسائل هنا واحدة في كل النظام: قل **ما حدث**
 * وما يُفعل، لا «فشل».
 *
 * ولا تسبق الخادم أبدًا: تُستعمل حين يغيب `detail_ar` فقط — نصّ الخادم العربي هو
 * الأصل ولا يُصاغ في العميل (backend-api §8).
 */
export function fallbackDetailAr(status: number): string {
  if (status === 401) return 'انتهت جلستك — سجّل الدخول من جديد.';
  if (status === 403) return 'لا صلاحية لهذا الإجراء.';
  if (status === 404) return 'لم يُعثر على المطلوب — قد يكون حُذف.';
  if (status === 413) return 'الملف أكبر مما يقبله الخادم.';
  if (status === 429) return 'محاولات كثيرة في وقت قصير — انتظر قليلًا ثم أعد المحاولة.';
  if (status === 502 || status === 503 || status === 504) {
    return 'الخادم لا يستجيب الآن (قد يكون في أثناء تحديث) — أعد المحاولة بعد لحظات.';
  }
  if (status >= 500) return 'عطل في الخادم — سُجِّل، وأعد المحاولة بعد قليل.';
  return 'حدث خطأ غير متوقع.';
}

async function readProblem(response: Response): Promise<Problem | undefined> {
  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('json')) return undefined;
  try {
    return (await response.json()) as Problem;
  } catch {
    return undefined;
  }
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers = new Headers({ accept: 'application/json' });
  let body: BodyInit | undefined;

  if (options.formData !== undefined) {
    body = options.formData;
  } else if (options.body !== undefined) {
    headers.set('content-type', 'application/json');
    body = JSON.stringify(options.body);
  }
  if (options.ifMatch !== undefined) headers.set('if-match', String(options.ifMatch));

  const init: RequestInit = {
    method: options.method ?? 'GET',
    credentials: 'include',
    headers,
    // الأصل نفسه دائمًا (الويب والـ API خلف نفس المضيف/الوكيل) — فحص Origin في الخادم.
    mode: 'same-origin',
    ...(body === undefined ? {} : { body }),
    ...(options.signal === undefined ? {} : { signal: options.signal }),
  };

  let response: Response;
  try {
    response = await fetch(buildUrl(path, options.query), init);
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new FlowApiError({
      code: FLOW_NETWORK,
      detailAr: 'تعذّر الوصول إلى الخادم — تحقّق من الاتصال.',
      status: 0,
    });
  }

  if (!response.ok) {
    const problem = await readProblem(response);

    /*
     * **جلسة انتهت لا تنتظر تبديل نافذة.** حارس الجلسة يقرأ `/auth/session` بـ
     * `staleTime: 60s`، فبلا لمسة أو تبديل لسان يبقى المالك أمام قشرة كاملة وبيانات
     * قديمة، وكل فعل يقابله خطأ لا يفهم سببه — والنظام يعرف الجواب: كل نداء يقول 401.
     *
     * فالإبطال يجعل الحارس **يسأل السلطة** (النقطة نفسها) بدل أن نستنتج نحن: 401
     * عابر من نقطة واحدة لا يُخرج المالك، وانتهاءٌ حقيقي يُعيده إلى `/login` مع حفظ
     * مقصده. والنقطة نفسها مستثناة وإلا أبطلت نفسها بلا نهاية.
     */
    if (response.status === 401 && path !== AUTH_SESSION_PATH) {
      void queryClient.invalidateQueries({ queryKey: qk.authSession() });
    }

    throw new FlowApiError({
      code: typeof problem?.code === 'string' ? problem.code : 'FLOW_INTERNAL',
      detailAr:
        typeof problem?.detail_ar === 'string' && problem.detail_ar !== ''
          ? problem.detail_ar
          : fallbackDetailAr(response.status),
      status: response.status,
      problem,
    });
  }

  if (response.status === 204) return undefined as T;
  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('json')) return undefined as T;
  return (await response.json()) as T;
}

export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> =>
    request<T>(path, { ...options, method: 'GET' }),
  /**
   * ‼️ الجسم الغائب يُرسَل `{}` لا «لا جسم» — عيب مقيس (مراجعة Codex 2026-07-30، F-01/F-02):
   * مسار يعلن مخطط body «اختياريًا» في Fastify يظل **يرفض غياب الجسم نفسه** بـ 400،
   * فكان «أنجزتها» و«ترقية فكرة» يفشلان من الويب بينما ينجحان بـ `{}` من curl.
   * القاعدة: `undefined` هنا تعني «لا حقول أرسلها» لا «لا طلب JSON» — والمسارات بلا
   * مخطط body (مثل `/activity/:id/undo`) تتجاهل `{}` بلا أثر، فالتوحيد آمن للصنفين.
   */
  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> =>
    request<T>(path, { ...options, method: 'POST', body: body ?? {} }),
  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> =>
    request<T>(path, { ...options, method: 'PATCH', body: body ?? {} }),
  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> =>
    request<T>(path, { ...options, method: 'PUT', body: body ?? {} }),
  delete: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> =>
    request<T>(path, { ...options, method: 'DELETE' }),
} as const;

/**
 * الإعداد الافتراضي القانوني (§1.3): TanStack Query هو مخزن حالة الخادم الوحيد.
 * `retry: 1` للقراءات و`retry: 0` للأوامر — الأمر يُعاد بيد المالك لا تلقائيًا،
 * فالتكرار الآمن مضمون عند الالتقاط وحده (مفتاح ULID).
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: true,
        retry: 1,
      },
      mutations: {
        retry: 0,
        /**
         * ‼️ `networkMode: 'always'` — عيب مقيس لا رأي (سيناريو E2E رقم 4، 2026-07-30):
         * الافتراضي `'online'` **يوقف دالة الطفرة نفسها** عند غياب الشبكة (onlineManager)،
         * فمسار الالتقاط offline كله — «القرص أولًا» في `use-capture-save` وصف الـ outbox
         * وشارة «بانتظار الإرسال ☁» — كان **كودًا ميتًا**: لا صف IndexedDB ولا أي أثر،
         * والكلمات تعيش في ذاكرة الطفرة الموقوفة وحدها وتضيع بإغلاق التبويب قبل عودة
         * الاتصال — وضياع التقاط هو الخطيئة التي بُني النظام لمنعها (§4.3، قرار O-2).
         * `'always'` يعيد القرار لدوالّنا: الالتقاط يكتب للقرص ثم يحاول الشبكة، وبقية
         * الأوامر تفشل فورًا بـ `FLOW_NETWORK` برسالة صادقة بدل توقف صامت يكذب تفاؤلُه.
         */
        networkMode: 'always',
      },
    },
  });
}

export const queryClient = createQueryClient();
